// utils/urlHelpers.js
import { useSearchParams } from "react-router-dom";


/**
 * Creates a URL with search and/or filter parameters
 * @param {string} basePath - The base path for the URL
 * @param {Object} options - Options object
 * @param {string} options.search - Search term to include
 * @param {Object} options.filters - Filters object to include
 * @param {Object} options.initialFilters - Initial filters to compare against (optional)
 * @returns {string} URL with query parameters
 */
export function createFilteredUrl(basePath, { search, filters, initialFilters = {} }) {
    const searchParams = new URLSearchParams();
    
    // Add search parameter
    if (search && search.trim()) {
      searchParams.set("search", search.trim());
    }
    
    // Add filters parameter
    if (filters && Object.keys(filters).length > 0) {
      // Only include filters that are different from initial filters
      const filtersToPass = { ...filters };
      
      Object.keys(filtersToPass).forEach(key => {
        if (JSON.stringify(filtersToPass[key]) === JSON.stringify(initialFilters[key])) {
          delete filtersToPass[key];
        }
      });
      
      if (Object.keys(filtersToPass).length > 0) {
        searchParams.set("filters", encodeURIComponent(JSON.stringify(filtersToPass)));
      }
    }
    
    const queryString = searchParams.toString();
    return queryString ? `${basePath}?${queryString}` : basePath;
  }
  
  /**
   * Hook to get URL parameters for search and filters
   * @returns {Object} Object containing urlSearch and urlFilters
   */
  export function useUrlParams() {
    const [searchParams] = useSearchParams();
    
    const urlSearch = searchParams.get("search") || "";
    
    let urlFilters = {};
    const filtersParam = searchParams.get("filters");
    if (filtersParam) {
      try {
        urlFilters = JSON.parse(decodeURIComponent(filtersParam));
      } catch (error) {
        console.error("Error parsing filters from URL:", error);
      }
    }
    
    return { urlSearch, urlFilters };
  }